/* eslint-disable no-restricted-globals */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Shield, Lock } from 'lucide-react';
import { permissionsApi, rolesApi } from '../api';
import type { PermissionCatalog, PermissionResource } from '../api';
import type { Role, RolePermission } from '../types';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { useConfirm } from '../components/ui/ConfirmProvider';

/** permission → scope. Kalit yo'q = ruxsat berilmagan. */
type Selection = Record<string, 'own' | 'shop'>;

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * Rollar va ruxsatlar.
 *
 * Matritsa KATALOGDAN chiziladi (`GET /api/permissions/`) — backendga yangi
 * modul qo'shilganda bu ekran o'zi yangilanadi, kodga tegilmaydi.
 */
export const Roles: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();

  const [roles, setRoles] = useState<Role[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [name, setName] = useState('');
  const [selection, setSelection] = useState<Selection>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, catalogRes] = await Promise.all([
        rolesApi.list(),
        permissionsApi.catalog(),
      ]);
      const data = Array.isArray(rolesRes.data)
        ? rolesRes.data
        : (rolesRes.data as any).results || [];
      setRoles(data);
      setCatalog(catalogRes.data);
    } catch {
      toast.error('Rollarni yuklab bo\'lmadi.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setSelection({});
    setOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditing(role);
    setName(role.name);
    const current: Selection = {};
    (role.permissions || []).forEach((p) => { current[p.permission] = p.scope; });
    setSelection(current);
    setOpen(true);
  };

  const toggle = (permission: string, resource: PermissionResource) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (permission in next) {
        delete next[permission];
        return next;
      }
      const scope = prev[resource.nav_permission] || resource.scopes[resource.scopes.length - 1];
      next[permission] = scope;
      // "Tahrirlay oladi, lekin ko'ra olmaydi" holati yaratilmasin.
      const view = `${resource.code}.view`;
      if (permission !== view && !(view in next)) {
        const viewable = resource.actions.some((a) => a.permission === view);
        if (viewable) next[view] = scope;
      }
      return next;
    });
  };

  const setScope = (resource: PermissionResource, scope: 'own' | 'shop') => {
    setSelection((prev) => {
      const next = { ...prev };
      resource.actions.forEach((a) => {
        if (a.permission in next) next[a.permission] = scope;
      });
      return next;
    });
  };

  const scopeOf = (resource: PermissionResource): 'own' | 'shop' => {
    const chosen = resource.actions
      .map((a) => selection[a.permission])
      .find(Boolean);
    return chosen || 'shop';
  };

  const selectedCount = useMemo(() => Object.keys(selection).length, [selection]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      const permissions: RolePermission[] = Object.entries(selection).map(
        ([permission, scope]) => ({ permission, scope }),
      );
      const payload = {
        name: name.trim(),
        code: editing ? editing.code : slugify(name) || 'rol',
        permissions,
      } as any;

      if (editing) {
        await rolesApi.update(editing.id, payload);
      } else {
        await rolesApi.create(payload);
      }
      setOpen(false);
      toast.success(editing ? 'Rol yangilandi' : 'Rol yaratildi');
      load();
    } catch (error: any) {
      const data = error.response?.data;
      const message =
        data?.name?.[0] || data?.code?.[0] || data?.error || data?.detail
        || 'Saqlashda xato';
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role: Role) => {
    const ok = await confirm({
      title: 'Rolni o\'chirish',
      description: `"${role.name}" o'chirilsinmi?`,
      confirmLabel: 'Ha, o\'chir',
      cancelLabel: 'Bekor',
      danger: true,
    });
    if (!ok) return;

    try {
      await rolesApi.delete(role.id);
      toast.success('Rol o\'chirildi');
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'O\'chirib bo\'lmadi');
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Rollar</h1>
        <button onClick={openCreate} className="btn btn-primary flex items-center gap-2 shrink-0">
          <Plus size={18} />
          <span className="hidden sm:inline">Yangi rol</span>
        </button>
      </div>

      {loading ? (
        <SkeletonRows rows={3} />
      ) : roles.length === 0 ? (
        <EmptyState icon={Shield} title="Rol yo'q" description="Birinchi rolni yarating." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((role) => (
            <div key={role.id} className="card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                    {role.name}
                    {role.is_system && <Lock size={14} className="text-gray-400 shrink-0" />}
                  </div>
                  <div className="text-xs text-gray-400 font-mono">{role.code}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(role)}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-50"
                    aria-label="Tahrirlash"
                  >
                    <Pencil size={16} />
                  </button>
                  {!role.is_system && (
                    <button
                      onClick={() => remove(role)}
                      className="p-2 rounded-lg text-gray-500 hover:bg-danger-50 hover:text-danger-600"
                      aria-label="O'chirish"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500">
                {(role.permissions || []).length} ta ruxsat
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Rolni tahrirlash' : 'Yangi rol'}
        size="lg"
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="role-name">Rol nomi</label>
            <input
              id="role-name"
              className="input"
              placeholder="Masalan: Katta sotuvchi"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="text-sm text-gray-500">
            Tanlangan: <span className="font-medium text-gray-900">{selectedCount}</span> ta ruxsat
          </div>

          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {catalog?.resources.map((resource) => {
              const hasAny = resource.actions.some((a) => a.permission in selection);
              return (
                <div key={resource.code} className="card p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="font-medium text-gray-900">{resource.label}</span>
                    {/* Qamrov resurs darajasida — har amalga alohida bo'lsa
                        matritsa o'qib bo'lmas holga keladi. */}
                    {resource.scopes.length > 1 && hasAny && (
                      <select
                        className="input py-1.5 text-sm w-auto"
                        value={scopeOf(resource)}
                        onChange={(e) => setScope(resource, e.target.value as 'own' | 'shop')}
                        aria-label={`${resource.label} qamrovi`}
                      >
                        {catalog.scopes
                          .filter((s) => resource.scopes.includes(s.code))
                          .map((s) => (
                            <option key={s.code} value={s.code}>{s.label}</option>
                          ))}
                      </select>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {resource.actions.map((action) => {
                      const checked = action.permission in selection;
                      return (
                        <label
                          key={action.permission}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                            checked
                              ? 'border-primary-600 bg-primary-50 text-primary-800'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="accent-primary-600"
                            checked={checked}
                            onChange={() => toggle(action.permission, resource)}
                          />
                          <span className="truncate">{action.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => setOpen(false)} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" disabled={saving || !name.trim()} className="btn btn-primary">
              {saving ? 'Saqlanmoqda…' : 'Saqlash'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
